import { BMPrimitives, BMText, useBMTheme } from "@traveloka/district-bloom";
import IcBagBaggageFill from "@traveloka/icon-kit-web/react/IcBagBaggageFill";
import IcSymbolChangeAirport from "@traveloka/icon-kit-web/react/IcSymbolChangeAirport";
import IcSymbolPassport from "@traveloka/icon-kit-web/react/IcSymbolPassport";
import IcSystemClock16 from "@traveloka/icon-kit-web/react/IcSystemClock16";
import htmr from "htmr";
import { useMemo } from "react";
import { StyleSheet } from "react-native";
import LinearGradient from "react-native-linear-gradient";
import BMView from "../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { formatMessage } from "../../../../utils";

type Props = {
  /**
   * Transit airport information.
   * If available, it means the flight has a transit.
   */
  internationalAirportName: string;
  /**
   * Transit duration information.
   * If available, it means the flight has a transit.
   */
  duration: string;
  /**
   * Whether the transit involves changing airport or not.
   * If available, it means the flight has a transit.
   */
  changeAirport?: Nullable<boolean>;
  /**
   * Whether visa is required for the transit or not.
   * If available, it means the flight has a transit.
   */
  visaRequired?: Nullable<boolean>;
  /**
   * Whether re-check-in is required for the transit or not.
   * If available, it means the flight has a transit.
   */
  mayNeedReCheckIn?: Nullable<boolean>;
};

export default function FlightTransitInfo({
  duration,
  changeAirport,
  visaRequired,
  mayNeedReCheckIn,
  internationalAirportName,
}: Props) {
  const { BMComponents } = useBMTheme();
  const { CorporateFlightTransitInfo: cr } = useMobileContentResource();

  const title = useMemo(() => {
    return htmr(
      formatMessage(cr.title, {
        airportName: internationalAirportName,
      }),
      {
        transform: {
          b: (node, _) => {
            if (
              !Array.isArray(node.children) ||
              node.children.length === 0 ||
              typeof node.children[0] !== "string" ||
              node.children[0] === ""
            )
              return null;
            return (
              <BMText ink="BMStyleTextInkHighlight">{node.children[0]}</BMText>
            );
          },
        },
      }
    );
  }, [internationalAirportName, cr.title]);

  const content = useMemo(() => {
    const res = [];
    if (changeAirport) {
      res.push({
        title: cr.changeAirportTitle,
        Icon: IcSymbolChangeAirport,
        ul: [cr.changeAirportUl1, cr.changeAirportUl2],
      });
    }
    if (mayNeedReCheckIn) {
      res.push({
        title: cr.recheckBaggageTitle,
        Icon: IcBagBaggageFill,
        ul: [cr.recheckBaggageUl1, cr.recheckBaggageUl2],
      });
    }
    if (visaRequired) {
      res.push({
        title: cr.visaRequiredTitle,
        Icon: IcSymbolPassport,
        ul: [cr.visaRequiredUl1],
      });
    }
    return res;
  }, [cr, visaRequired, mayNeedReCheckIn]);

  return (
    <LinearGradient
      end={{ x: 0.7, y: 0.7 }}
      start={{ x: 0, y: 0 }}
      colors={["#F7FCDE", "#FFFFFF"]}
      style={styles.container}
    >
      <BMView spacing="md" style={styles.content}>
        <BMView row justify="between">
          <BMView flex>
            <BMText variant="uiNormal">{title}</BMText>
          </BMView>
          {duration ? (
            <BMView row spacing="xs" align="center">
              <IcSystemClock16
                width={12}
                height={12}
                color={BMComponents.BMStyleTextInkSecondary}
              />
              <BMText variant="uiSmall">{duration}</BMText>
            </BMView>
          ) : null}
        </BMView>
        {content.length ? (
          <BMView spacing="md">
            {content.map((item, index) => (
              <BMView key={`transit-info-${index}`} spacing="xs">
                <BMView row spacing="xs" align="center">
                  <item.Icon
                    width={12}
                    height={12}
                    color={BMComponents.BMStyleIconInkPrimary}
                  />
                  <BMText variant="uiNormal">{item.title}</BMText>
                </BMView>
                <BMView flex>
                  {item.ul.length > 1 ? (
                    item.ul.map((li, liIndex) => (
                      <BMView
                        row
                        spacing="xs"
                        style={styles.li}
                        key={`li-${liIndex}`}
                      >
                        <BMText
                          variant="paragraphSmall"
                          ink="BMStyleTextInkSecondary"
                        >
                          •
                        </BMText>
                        <BMText
                          variant="paragraphSmall"
                          ink="BMStyleTextInkSecondary"
                        >
                          {li}
                        </BMText>
                      </BMView>
                    ))
                  ) : (
                    <BMText
                      variant="paragraphSmall"
                      ink="BMStyleTextInkSecondary"
                    >
                      {item.ul[0]}
                    </BMText>
                  )}
                </BMView>
              </BMView>
            ))}
          </BMView>
        ) : null}
      </BMView>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: BMPrimitives.BMRadius.sm,
  },
  content: {
    padding: BMPrimitives.BMSize.md,
  },
  li: {
    paddingLeft: BMPrimitives.BMSize.sm,
  },
});
